import { useContext, useEffect, useState } from 'react';
import styled from 'styled-components';
import ModalDrawer from 'components/microLayouts/drawer';
import Button from 'components/button';
import { format } from 'date-fns';
import { formatCreatedBy, onDownloadReport, parseSize } from '../shared/utils';
import ReportMobileListItem from 'pages/Reporting/components/shared/ReportMobileListItem';
import { getPopularReportImg } from 'utils/imageHandling';
import StatusCell from 'pages/Reporting/components/ReportingHistory/StatusCell';
import ButtonIcon from 'components/buttonIcon';
import { reportStatus } from 'contexts/reporting/utils';
import InputText from 'pages/Reporting/components/InputText';
import { AppContext } from 'contexts/app';

const ReportMobileDrawerContainer = styled.div(
  ({ theme }) => `
    height: 100%;
    background: ${theme.default.colors.palette.Business_LightThemeGreyLighter};
    .drawer {
      &__header {
        margin-top: 10px;
        background: var(--secondary-promo-purple-gradient);
        padding: 25px 10px;

        h5 {
          font-size: 20px;
          font-style: normal;
          font-weight: 700;
          line-height: 26px;
          color: white;
        }
      }

      &__body {
        padding: 15px;
        height: 100%;
        display: flex;
        flex-direction: column;

        .form-group {
          display: flex;
          gap: 1px;
          margin-top: 5px;

          > * {
            display: flex;
            align-items: center;
            height: 54px;
            background: white;
            padding: 10px;
            font-size: 14px;
            font-style: normal;
          }

          &-label {
            width: 40%;
            color: ${theme.default.colors.palette.Business_LightThemeGreyDarker};
            font-weight: 500;

            &--editable {
              padding-right: 30px;
            }
          }

          &-value {
            width: 60%;
            font-weight: 700;

            &--read-only {
              padding-left: 20px;
            }

            input {
              font-size: inherit;
              font-style: inherit;
              font-weight: inherit;
              background: none;
              white-space: inherit;
              overflow: inherit;
              text-overflow: inherit;
            }
          }
        }
      }

      &__actions {
        display: flex;
        gap: 10px;
        position: fixed;
        bottom: 80px;
        left: 7px;
        right: 15px;   
        background: ${theme.default.colors.palette.Business_LightThemeGreyLighter};
        button {
          width: 50%;
        } 
      }
    }
  `
);

const ReportMobileListContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin-top: 12px;
`;

interface Report {
  id: string;
  name: string;
  created_by: any;
  scheduled: boolean;
  status: string;
  created_at: string | Date;
  size: number;
  type?: string;
  is_popular_report?: boolean;
}

interface Reports {
  results: Report[];
}

interface ReportingHistoryMobileListProps {
  reports: Reports;
  loading: boolean;
  onDeleteReport: (id: string, name: string) => void;
  onUpdateReport: (id: string, key: string, value: any) => Promise<void>;
}

const ReportingHistoryMobileList: React.FC<ReportingHistoryMobileListProps> = ({
  reports,
  loading,
  onDeleteReport,
  onUpdateReport,
}) => {
  const { setBackButton } = useContext(AppContext);
  const [reportDetails, setReportDetails] = useState(null);

  const onReportDetail = (report) => {
    setReportDetails(report);
    setBackButton({
      action: () => {
        setReportDetails(null);
        setBackButton(null);
      },
    });
  };

  const handleUpdate = async (id, key, value) => {
    await onUpdateReport(id, key, value);
    setReportDetails((prev) => ({ ...prev, [key]: value }));
  };

  return (
    <>
      <ReportMobileListContainer style={{ opacity: loading ? 0.5 : 1 }}>
        {reports.results.map((report) => {
          const label = (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
              {report.is_popular_report && (
                <span
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 32,
                    height: 32,
                    borderRadius: 8,
                    background: 'rgba(167, 147, 246, 0.12)',
                  }}
                >
                  <img
                    src={getPopularReportImg(report.type)}
                    alt={`${report.type} popular report`}
                    width={18}
                    height={18}
                    style={{ display: 'inline-block' }}
                  />
                </span>
              )}
              <span>{report.name}</span>
            </span>
          );
          return (
            <ReportMobileListItem
              key={report.id}
              label={label}
              subLabel={<StatusCell status={report.status} variant="mobile" />}
              onReportDetail={() => onReportDetail(report)}
              rightContent={
                <ButtonIcon
                  icon="Download"
                  $secondary
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    onDownloadReport(report.id);
                  }}
                  disabled={loading || report.status !== reportStatus.completed}
                />
              }
            />
          );
        })}
      </ReportMobileListContainer>
      <ModalDrawer
        closable={false}
        placement="right"
        visible={!!reportDetails}
        destroyOnClose
        zIndex={100}
        width="100%"
        contentWrapperStyle={{ filter: loading ? 'blur(1px)' : 'none' }}
        bodyStyle={{ padding: '64px 0 70px' }}
      >
        {reportDetails && (
          <ReportMobileDrawerContainer>
            <div className="drawer__header">
              <h5>{reportDetails.name}</h5>
            </div>
            <div className="drawer__body">
              <div className="form-group">
                <label className="form-group-label form-group-label--editable">Report name</label>
                <div className="form-group-value">
                  {reportDetails.is_popular_report ? (
                    <div className="form-group-value--read-only">{reportDetails.name}</div>
                  ) : (
                    <InputText
                      value={reportDetails.name}
                      disabled={loading}
                      editIconOutside
                      onSave={(value) => handleUpdate(reportDetails.id, 'name', value)}
                    />
                  )}
                </div>
              </div>
              <div className="form-group">
                <label className="form-group-label">Created by</label>
                <div className="form-group-value form-group-value--read-only">
                  {formatCreatedBy(reportDetails.created_by, reportDetails.scheduled)}
                </div>
              </div>
              <div className="form-group">
                <label className="form-group-label">Status</label>
                <div className="form-group-value form-group-value--read-only">
                  <StatusCell status={reportDetails.status} variant="mobile" />
                </div>
              </div>
              <div className="form-group">
                <label className="form-group-label">Created</label>
                <div className="form-group-value form-group-value--read-only">
                  {format(new Date(reportDetails.created_at), 'HH:mmaa yyyy-MM-dd')}
                </div>
              </div>
              <div className="form-group">
                <label className="form-group-label">Size</label>
                <div className="form-group-value form-group-value--read-only">
                  {parseSize(reportDetails.size)}
                </div>
              </div>
            </div>
            <div className="drawer__actions">
              <Button
                $secondary
                onClick={() => onDeleteReport(reportDetails.id, reportDetails.name)}
              >
                Remove report
              </Button>
              <Button $primary onClick={() => onDownloadReport(reportDetails.id)}>
                Download report
              </Button>
            </div>
          </ReportMobileDrawerContainer>
        )}
      </ModalDrawer>
    </>
  );
};

export default ReportingHistoryMobileList;
